import React, { useState, useEffect } from 'react';
import { Loader2, X, CheckCircle2, Flag, ImageOff, Sparkles } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { diagnosticsSnapshot } from '../../utils/diagnosticsBuffer';

const FIELD = 'w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white';

export default function ReportIssueModal({ open, onClose, issueContext, fingerprint, screenshotDataUrl }) {
  const [draft, setDraft] = useState({ subject: '', category: 'bug', priority: 'normal', summary: '', what_happened: '', what_i_expected: '', steps_to_reproduce: '' });
  const [drafting, setDrafting] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // {duplicate?, id, status}
  const [error, setError] = useState(null);
  const [shot, setShot] = useState(screenshotDataUrl);
  const [diags] = useState(() => diagnosticsSnapshot());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.post('/copilot/escalate/suggest', {
          conversation: [],
          issue_context: { ...(issueContext || {}), diagnostics: diags },
          page_context: { url: window.location.href, title: document.title },
        });
        if (!cancelled && data?.draft) setDraft((d) => ({ ...d, ...data.draft }));
      } catch (_) {
        if (!cancelled) {
          setDraft((d) => ({
            ...d,
            subject: issueContext?.title || 'Issue on ' + (document.title || 'AkountFly'),
            summary: issueContext?.description || 'Something went wrong on this page.',
          }));
        }
      } finally {
        if (!cancelled) setDrafting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const submit = async () => {
    setSending(true); setError(null);
    try {
      let imageFileIds = [];
      if (shot) {
        try {
          const blob = await (await fetch(shot)).blob();
          const form = new FormData();
          form.append('file', new File([blob], 'issue-screenshot.jpg', { type: 'image/jpeg' }));
          const up = await apiClient.post('/copilot/upload-image', form);
          if (up.data?.file_id || up.data?.id) imageFileIds = [up.data.file_id || up.data.id];
        } catch (_) { /* screenshot upload is best-effort */ }
      }
      const { data } = await apiClient.post('/copilot/escalate', {
        ...draft,
        additional_context: draft.additional_context || null,
        image_file_ids: imageFileIds,
        page_context: { url: window.location.href, title: document.title },
        source: 'auto_error_cta',
        fingerprint,
        diagnostics: { ...diags, issue_context: issueContext || {} },
        ai_generated: true,
      });
      setResult(data);
    } catch (e) {
      setError(e?.normalised?.message || e?.response?.data?.detail || 'Failed to send - please try again');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="report-issue-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Flag className="w-4 h-4 text-rose-600" />
            Report this issue
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-400" data-testid="report-issue-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {result ? (
          <div className="p-6 text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            {result.duplicate ? (
              <>
                <p className="text-sm font-semibold text-slate-800">You've already reported this issue</p>
                <p className="text-xs text-slate-500">Status: <span className="font-semibold capitalize">{result.status?.replace('_', ' ')}</span> - our team is on it. You'll get an email when it's resolved.</p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-slate-800">Report sent to the AkountFly team</p>
                <p className="text-xs text-slate-500">Reference <span className="font-mono font-semibold">{(result.id || '').slice(0, 8).toUpperCase()}</span>. Track it in Help Center → My Reports. You'll get an email when it's resolved.</p>
              </>
            )}
            <button type="button" onClick={onClose} className="mt-1 px-4 py-1.5 rounded-md bg-slate-800 text-white text-xs font-semibold" data-testid="report-issue-done">Done</button>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 space-y-3 overflow-y-auto flex-1">
              {drafting && (
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                  <Loader2 className="w-3 h-3 animate-spin" />
                  AkountFly is reading the issue and drafting your report…
                </div>
              )}
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">Subject</label>
                <input className={FIELD} value={draft.subject || ''} onChange={set('subject')} data-testid="report-subject-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase">Category</label>
                  <select className={FIELD} value={draft.category || 'bug'} onChange={set('category')} data-testid="report-category-select">
                    {['bug', 'data', 'billing', 'tax', 'onboarding', 'other'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase">Priority</label>
                  <select className={FIELD} value={draft.priority || 'normal'} onChange={set('priority')} data-testid="report-priority-select">
                    {['low', 'normal', 'high', 'urgent'].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">Summary</label>
                <textarea className={FIELD} rows={3} value={draft.summary || ''} onChange={set('summary')} data-testid="report-summary-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">What happened</label>
                <textarea className={FIELD} rows={2} value={draft.what_happened || ''} onChange={set('what_happened')} data-testid="report-what-happened-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">What you expected</label>
                <textarea className={FIELD} rows={2} value={draft.what_i_expected || ''} onChange={set('what_i_expected')} data-testid="report-expected-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">Screenshot attached</label>
                {shot ? (
                  <div className="relative inline-block mt-1">
                    <img src={shot} alt="Page screenshot" className="h-24 rounded-md border border-slate-200 object-cover" data-testid="report-screenshot-preview" />
                    <button
                      type="button"
                      onClick={() => setShot(null)}
                      className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-0.5 shadow text-slate-500 hover:text-rose-600"
                      title="Remove screenshot"
                      data-testid="report-screenshot-remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1"><ImageOff className="w-3.5 h-3.5" /> No screenshot attached</div>
                )}
              </div>
              {error && <p className="text-xs text-rose-600" data-testid="report-issue-error">{error}</p>}
            </div>
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
              <p className="text-[10px] text-slate-400">Diagnostics from this page are attached automatically. Doesn't use your AI allowance.</p>
              <button
                type="button"
                onClick={submit}
                disabled={sending || drafting || !(draft.subject || '').trim()}
                className="px-4 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 flex-shrink-0"
                data-testid="report-issue-submit"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
                Send report
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
